import React from 'react';
import { Alert, Divider } from '@mui/material';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Button, Input } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import { FacebookButton } from '../../../FacebookButton';
import type { FacebookAuthResponse } from '../../../../api';
import ReCaptchaV3 from '../../../Wrappers/ReCaptchaV3';
import { DISABLE_CAPTCHA } from '../../../../utils';
import type { EmailFormValues } from './useEmailStep';

type AuthEmailStepViewProps = {
    register: UseFormRegister<EmailFormValues>;
    errors: FieldErrors<EmailFormValues>;
    loading?: boolean;
    defaultEmail?: string;
    error?: string | null;
    dismissError?: () => void;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
    onFacebookSuccess?: (user: FacebookAuthResponse) => void;
};

export const AuthEmailStepView: React.FC<AuthEmailStepViewProps> = ({
    register,
    errors,
    loading,
    defaultEmail,
    error,
    dismissError,
    onSubmit,
    onFacebookSuccess,
}) => (
    <form id="auth-email-form" onSubmit={onSubmit}>
        <div
            id="auth-email-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
        >
            <div
                id="auth-email-facebook-section"
                className="flex flex-col items-center"
            >
                <div
                    id="auth-email-facebook-heading"
                    className="text-body-large text-primary mb-6"
                >
                    Join or Sign in with Facebook
                </div>
                <FacebookButton onSuccess={onFacebookSuccess} />
                <div
                    id="auth-email-facebook-speed-description"
                    className="flex flex-col items-center text-body-xsmall text-secondary mt-3"
                >
                    Fast and easy. No password required.
                </div>
                <div
                    id="auth-email-facebook-privacy-description"
                    className="flex flex-col items-center text-body-xsmall text-secondary"
                >
                    No posts on Facebook without permission.
                </div>
            </div>
            <Divider
                id="auth-email-method-divider"
                className="text-primary text-body-small mt-6 mb-5"
            >
                Or
            </Divider>
            <div
                id="auth-email-address-section"
                className="flex flex-col gap-3 items-center"
            >
                <div
                    id="auth-email-address-heading"
                    className="text-body-large text-primary mb-6"
                >
                    Join or Sign in with your email
                </div>
                <Input
                    {...register('email', {
                        required: 'Please enter a valid email address',
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Please enter a valid email address',
                        },
                    })}
                    type="email"
                    label="Email Address"
                    defaultValue={defaultEmail}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    id="auth-email"
                />
            </div>
            {error ? (
                <Alert
                    id="auth-email-error-alert"
                    className="mt-6"
                    data-testid="wallet-error-alert"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            {!DISABLE_CAPTCHA && <ReCaptchaV3 hideText={true} />}
            <Button
                id="auth-email-continue-button"
                type="submit"
                className="w-full mt-8"
                loading={loading}
            >
                Continue
            </Button>
        </div>
    </form>
);
